import { useEffect } from "react";
import Intercom, { shutdown } from "@intercom/messenger-js-sdk";
import { useAuthStore } from "../stores/auth";
import { supabase } from "../lib/supabase";

const INTERCOM_APP_ID = "chghheu9";

interface IntercomBootData {
  jwt: string;
  custom_attributes: Record<string, unknown>;
}

async function fetchIntercomBoot(): Promise<IntercomBootData | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) return null;

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

  const res = await fetch(`${supabaseUrl}/functions/v1/intercom-user-hash`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      apikey: supabaseAnonKey,
      "Content-Type": "application/json",
    },
    body: "{}",
  });

  if (!res.ok) {
    const body = await res.text();
    console.error("Intercom JWT fetch failed", res.status, body);
    return null;
  }

  const data = (await res.json()) as Partial<IntercomBootData>;
  if (!data.jwt) return null;
  return {
    jwt: data.jwt,
    custom_attributes: data.custom_attributes ?? {},
  };
}

export function IntercomMessenger() {
  const user = useAuthStore((s) => s.user);
  const initialized = useAuthStore((s) => s.initialized);

  useEffect(() => {
    if (!initialized || !user) return;

    let cancelled = false;
    let booted = false;

    (async () => {
      const boot = await fetchIntercomBoot();
      if (cancelled || !boot) return;

      // Mirror the signed custom_attributes on the client boot so they
      // show up on the user's Intercom profile straight away. They're
      // also present as signed JWT claims, so Intercom trusts them.
      Intercom({
        app_id: INTERCOM_APP_ID,
        intercom_user_jwt: boot.jwt,
        ...boot.custom_attributes,
      });
      booted = true;
    })();

    return () => {
      cancelled = true;
      if (booted) shutdown();
    };
  }, [initialized, user]);

  return null;
}
